import { useEffect, useRef } from 'preact/hooks';
import Button from 'components/Button';
import LoadingDots from 'components/LoadingDots';
import {
  useGlobalConfigContext,
  usePTPConfigContext,
} from 'GlobalConfig.context';
import { useTranslation } from 'react-i18next';
import { CustomTextDefault } from 'components/CustomText';
import { getDefaultCustomText } from 'components/CustomText/CustomText.util';
import { useShipmentLabel } from './useShipmentLabel';
import {
  ErrorContainer,
  ExclamationMarkIcon,
  InfoLabel,
  QRImage,
} from './ShipmentLabelDisplay.styles';

// Flow overview: see useShipmentLabel.ts

interface ShipmentLabelDisplayProps {
  shipmentPk: number;
  // When false, the QR image is suppressed and the button is disabled.
  // Sample usage: Use this when booking has not completed successfully (failed or still pending).
  interactive?: boolean;
}

export function ShipmentLabelDisplay({
  shipmentPk,
  interactive = true,
}: ShipmentLabelDisplayProps) {
  const { t } = useTranslation();
  const { organizationId } = useGlobalConfigContext();

  const {
    return: { customText },
  } = usePTPConfigContext();

  // ARTEMIS-6557: operator-configured CTA label, else i18n default —
  // mirrors the parseLegacyCustomText `text || defaultText` fallback (D-001).
  // Optional chaining: pp-constants may not yet ship the labelDownloadCta
  // default, so labelDownloadCta can be undefined at runtime (D-005/P-004).
  const downloadLabel =
    customText?.labelDownloadCta?.text ||
    getDefaultCustomText(CustomTextDefault.LabelDownloadCta, t);

  const {
    isQR,
    qrImageSrc,
    qrImageError,
    isLoading,
    showError,
    downloadSuccess,
    handleNonQRClick,
    handleQRClick,
  } = useShipmentLabel({
    orgId: organizationId,
    shipmentPk,
  });

  // Focus refs for error/success states — move focus when button is replaced
  // so keyboard/AT users are not left stranded at <body> after clicking.
  const errorRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showError) {
      errorRef.current?.focus();
    }
  }, [showError]);

  useEffect(() => {
    if (downloadSuccess) {
      successRef.current?.focus();
    }
  }, [downloadSuccess]);

  const renderStatus = () => {
    if (showError) {
      return (
        <ErrorContainer>
          <InfoLabel tabIndex={-1} ref={errorRef}>
            <ExclamationMarkIcon />
            {t('RETURN_LABEL_DOWNLOAD_ERROR_TEXT')}
          </InfoLabel>
        </ErrorContainer>
      );
    }

    if (downloadSuccess) {
      return (
        <InfoLabel tabIndex={-1} ref={successRef}>
          {t('RETURN_DOWNLOAD_SUCCESSFUL')}
        </InfoLabel>
      );
    }

    return (
      <Button
        block
        variant="fulfilled"
        disabled={!interactive || isLoading}
        aria-busy={isLoading}
        data-testid="cta-download-label"
        onClick={isQR ? handleQRClick : handleNonQRClick}
      >
        {isLoading ? <LoadingDots /> : downloadLabel}
      </Button>
    );
  };

  return (
    <>
      {/* QR image: only rendered when validated by the hook (see useShipmentLabel.ts).
          qrImageError is true when base64 is corrupt/truncated — hides the image
          and blocks download. */}
      {interactive && isQR && qrImageSrc && !qrImageError && (
        <QRImage src={qrImageSrc} alt="" />
      )}
      {renderStatus()}
    </>
  );
}
